import { useRef } from 'react'
import { Box, Stack, Typography, CircularProgress, Alert, Button } from '@mui/material'
import { Link } from 'react-router-dom'
import {
  useOwnedCollections,
  useSharedCollections,
  useCollectionPins,
} from '~/api/hooks/useCollections'
import { ChartFromPin } from '~/components/Charts/ChartFromPin'
import { PageHeader } from '~/components/Navigation/PageHeader'
import { PageExportButtons } from '~/components/Export/PageExportButtons'
import { CollectionDataExportButton } from '~/components/Export/CollectionDataExportButton'
import { ROUTES } from '~/constants/routes'
import { buildExportFilenameBase } from '~/util/export/filename'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

export function Home() {
  const owned = useOwnedCollections()
  const shared = useSharedCollections()
  // Dashboard = the user's own is_dashboard collection, else one shared with them. The shared
  // fallback is the onboarding stopgap until there's an admin interface: it lets an admin (e.g.
  // Christoph) give everyone a Start collection by sharing it + marking it as a dashboard. Owned
  // always wins, so a user can override with their own. Reuses is_dashboard; the durable design is
  // a dedicated admin-set "default dashboard" flag (revisit with the admin UI).
  const dashboard =
    owned.data?.find((c) => c.is_dashboard) ?? shared.data?.find((c) => c.is_dashboard) ?? null
  const pins = useCollectionPins(dashboard?.id ?? null)
  const gridRef = useRef<HTMLDivElement>(null)

  if (owned.isLoading || shared.isLoading) {
    return (
      <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (owned.isError || shared.isError) {
    return (
      <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
        <PageHeader title={DIALOGS.dashboardPageTitle} />
        <Alert severity="error">{DIALOGS.couldNotLoadCollections}</Alert>
      </Box>
    )
  }

  if (!dashboard) {
    return (
      <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
        <PageHeader title={DIALOGS.dashboardPageTitle} />
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {DIALOGS.noDashboardYet}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {DIALOGS.emptyDashboardHint}
          </Typography>
          <Button component={Link} to={ROUTES.collections} variant="contained">
            {DIALOGS.toDashboard}
          </Button>
        </Box>
      </Box>
    )
  }

  const dashboardName = dashboard.name || 'dashboard'
  const filenameBase = buildExportFilenameBase({
    scope: 'page',
    page: 'dashboard',
    dashboard: dashboardName,
  })
  const validPins = (pins.data ?? []).flatMap((r) => ('pin' in r ? [r.pin] : []))

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        data-export-skip="true"
      >
        <PageHeader title={dashboard.name || DIALOGS.dashboardPageTitle} />
        <Stack direction="row" spacing={1} alignItems="center">
          <PageExportButtons
            target={() => gridRef.current}
            filenameBase={filenameBase}
            disabled={!pins.data || pins.data.length === 0}
          />
          <CollectionDataExportButton
            pins={validPins}
            filenameBase={filenameBase}
            disabled={!pins.data || pins.data.length === 0}
          />
        </Stack>
      </Stack>
      {pins.isLoading ? <CircularProgress /> : null}
      {pins.isError ? <Alert severity="error">{DIALOGS.couldNotLoadPins}</Alert> : null}
      {pins.data && pins.data.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4 }}>
          {DIALOGS.emptyDashboard}
        </Typography>
      ) : null}
      {pins.data && pins.data.length > 0 ? (
        <Stack spacing={3} ref={gridRef}>
          {pins.data.map((result) => (
            <Box
              key={result.row.id}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}
            >
              {'pin' in result ? (
                <ChartFromPin pin={result.pin} />
              ) : (
                <Alert severity="warning">
                  {DIALOGS.pinLoadFailed}
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {result.error}
                  </Typography>
                </Alert>
              )}
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  )
}
